import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../store/AuthContext";
import axios from "axios";
import { getVideos, updateVideo } from "../api/videosApi";
import { getClasses, getSubjects } from "../api/classSubjectApi";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  MenuItem,
  CircularProgress,
  Container,
  Paper,
  useTheme,
  useMediaQuery,
  Alert,
  InputAdornment,
  Grid
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  Edit,
  Title,
  Description,
  Link as LinkIcon,
  School,
  Subject as SubjectIcon,
  ArrowBack,
  CloudUpload
} from "@mui/icons-material";
import { getTopics } from "../api/topicsApi";
import TopicIcon from "@mui/icons-material/Topic";

export default function TeacherEditVideo() {
  const { token } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
    const [topics, setTopics] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    class_id: "",
    subject_id: "",
    topic_id: "",
    file_path: "",
  });

  useEffect(() => {
    loadData();
    loadTopics();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load dropdowns
      const classRes = await getClasses(token);
      const subjectRes = await getSubjects(token);

      setClasses(classRes.data);
      setSubjects(subjectRes.data);

      // Load the video
      const videoRes = await getVideos(token, { id });
      const v = videoRes.data[0] || videoRes.data;

      setForm({
        title: v.title || "",
        description: v.description || "",
        class_id: v.class_id || "",
        subject_id: v.subject_id || "",
        topic_id: v.topic_id || "",
        file_path: v.file_path || "",
      });

    } catch (err) {
      console.error("Error loading video", err);
      setError("Cannot load video data");
    } finally {
      setLoading(false);
    }
  };

  const loadTopics = async () => {
      try {
        const res = await axios.get(
          "https://lms.childinfo.in/api/topics.php",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setTopics(res.data || []);
      } catch (err) {
        console.error("Error loading topics", err);
      }
    };


  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const validateForm = () => {
    if (!form.title.trim()) {
      setError("Title is required");
      return false;
    }
    if (!form.class_id) {
      setError("Please select a class");
      return false;
    }
    if (!form.subject_id) {
      setError("Please select a subject");
      return false;
    }
    if (!form.topic_id) {
      setError("Please select a topic");
      return false;
    }
    if (!form.file_path.trim()) {
      setError("Video URL is required");
      return false;
    }
    return true;
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    setUpdating(true);
    setError("");
    setSuccess("");

    try {
      await updateVideo(token, id, form);
      setSuccess("Video updated successfully!");
      
      // Navigate after 2 seconds
      setTimeout(() => {
        navigate("/teacher");
      }, 2000);
    } catch (err) {
      console.error(err);
      setError("Update failed. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const getYouTubeThumbnail = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg` : null;
  };

  const thumbnailUrl = getYouTubeThumbnail(form.file_path);

  const filteredTopics = topics.filter(
    t => String(t.subject_id) === String(form.subject_id) && t.status === "Active"
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress size={60} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          mb: { xs: 2, sm: 3, md: 4 },
          background: "linear-gradient(135deg, #FF9800, #F57C00)",
          color: "white",
          borderRadius: { xs: 2, sm: 3 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          alignItems: { xs: "flex-start", sm: "center" }, 
          justifyContent: "space-between", 
          gap: 2 
        }}>
          <Box>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ 
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 1
              }}
            >
              <Edit sx={{ fontSize: { xs: 32, sm: 40 } }} />
              Edit Video
            </Typography>
            <Typography variant={isMobile ? "body2" : "h6"} sx={{ opacity: 0.9 }}>
              Update video details and information
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            size={isMobile ? "medium" : "large"}
            startIcon={<ArrowBack />}
            onClick={() => navigate("/teacher")}
            sx={{
              background: "rgba(255,255,255,0.1)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: "bold",
              px: { xs: 2, sm: 3 },
              py: 1,
              "&:hover": {
                background: "rgba(255,255,255,0.2)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
              minWidth: { xs: "100%", sm: "auto" }
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Form Section */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}>
                Edit Video Details
              </Typography>

              <TextField
                fullWidth
                label="Video Title"
                name="title"
                margin="normal"
                value={form.title}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Title color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                margin="normal"
                multiline
                rows={3}
                value={form.description}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              />

              {/* CLASS DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Class"
                name="class_id"
                margin="normal"
                value={form.class_id}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <School color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.class}
                  </MenuItem>
                ))}
              </TextField>

              {/* SUBJECT DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Subject"
                name="subject_id"
                margin="normal"
                value={form.subject_id}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SubjectIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                {subjects.map((sub) => (
                  <MenuItem key={sub.id} value={sub.id}>
                    {sub.subject}
                  </MenuItem>
                ))}
              </TextField>

              {/* TOPIC DROPDOWN */}
              <TextField
                fullWidth
                select
                label="Select Topic"
                name="topic_id"
                margin="normal"
                value={form.topic_id}
                onChange={handleChange}
                disabled={!form.subject_id}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TopicIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
              >
                <MenuItem value="">
                  <em>{form.subject_id ? "Select a topic" : "Select subject first"}</em>
                </MenuItem>

                {filteredTopics.map((topic) => (
                  <MenuItem key={topic.id} value={topic.id}>
                    {topic.topic_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                label="YouTube Video URL"
                name="file_path"
                margin="normal"
                value={form.file_path}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                size={isMobile ? "small" : "medium"}
                helperText="Enter a valid YouTube URL"
              />

              <Button
                variant="contained"
                fullWidth
                size={isMobile ? "medium" : "large"}
                startIcon={updating ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                onClick={handleUpdate}
                disabled={updating}
                sx={{
                  mt: 3,
                  py: 1.5,
                  background: "linear-gradient(45deg, #FF9800, #F57C00)",
                  fontWeight: "bold",
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  "&:hover": {
                    background: "linear-gradient(45deg, #F57C00, #EF6C00)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {updating ? "Updating..." : "Update Video"}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid rgba(0,0,0,0.05)",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: "bold", color: "primary.main" }}>
                Current Video Preview
              </Typography>

              {thumbnailUrl ? (
                <Box>
                  {/* Thumbnail Preview */}
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 3,
                      border: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      component="img"
                      src={thumbnailUrl}
                      alt="Video thumbnail"
                      sx={{
                        width: "100%",
                        height: { xs: 180, sm: 200, md: 240 },
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>

                  {/* Preview Details */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      {form.title || "No title"}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                      {form.description || "No description"}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {form.class_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <School fontSize="small" color="action" />
                          <Typography variant="body2">
                            Class: {classes.find(c => c.id === form.class_id)?.class || "N/A"}
                          </Typography>
                        </Box>
                      )}
                      
                      {form.subject_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <SubjectIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Subject: {subjects.find(s => s.id === form.subject_id)?.subject || "N/A"}
                          </Typography>
                        </Box>
                      )}

                      {form.topic_id && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TopicIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            Topic: {topics.find(t => t.id === form.topic_id)?.topic_name || "N/A"}
                          </Typography>
                        </Box>
                      )}

                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    height: { xs: 180, sm: 200, md: 240 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    borderRadius: 2,
                    mb: 3,
                  }}
                >
                  <Box textAlign="center">
                    <LinkIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No thumbnail available
                    </Typography>
                  </Box>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Changes will be reflected immediately for your students.
                  Make sure the YouTube video is publicly accessible.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}